import { cn } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import InDecGroupBox from '@/components/ui/buttons/indec-group';
import InputCheck from '@/components/ui/inputs/input-check';
import InputRadio from '@/components/ui/inputs/input-radio';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { useForm, Controller } from 'react-hook-form';
import { InputSelect } from '@/components/ui/inputs/input-select';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { getFileUrl } from '@/services/CommonService';
import { useSelector, useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import CommonModal from '@/components/ui/modals/CommonModal';
import { addOrUpdateCartItemTable } from '@/store/actions';
import ToastMe from '@/components/ui/ToastMe';

const ProductInfoModal = ({ modalOpen, setModalOpen, selectProductData = {}, handleProductCauction, setSelectProductData }) => {
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm();

  // We need generalSetting for currency formatting
  const { generalSetting } = useSelector((state) => state?.GeneralSetting || {});
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [orderItems, setOrderItems] = useState({});
  const [addToCartItem, setAddToCartItem] = useState({});
  const [sizeVariationValue, setSizeVariationValue] = useState([]);

  // Helper: consistent currency string for inline labels (no hard-coded "$")
  const formatCurrency = (val, gs) => {
    const sym =
      (gs?.site_default_currency_symbol ??
        gs?.site_default_currency?.symbol ??
        gs?.currency?.symbol ??
        '$')
        .toString()
        .trim();
    const decimals = Number.isInteger(Number(gs?.site_digit_after_decimal_point))
      ? Number(gs?.site_digit_after_decimal_point)
      : 2;
    const pos = String(gs?.site_currency_position || '1');
    const num = Number(val ?? 0).toFixed(decimals);
    return pos === '2' ? `${num} ${sym}` : `${sym} ${num}`;
  };

  useEffect(() => {
    if (selectProductData?._id && selectProductData?._id !== '') {
      let itemVariation = { _ids: [], name: [], price: [] };
      let itemVariationTotal = 0;
      let itemTotal = 0;

      if (selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 1) {
        const varion_ids = [];
        const varion_name = [];
        const varion_price = [];
        const varion_attribute = [];
        const varion_attribute_name = [];

        selectProductData?.item_variation_attributes.forEach((element) => {
          const itemVarionDefaultSelect = selectProductData?.item_variations.find((item) => item.item_attribute_id === element._id);
          if (itemVarionDefaultSelect && element?._id === itemVarionDefaultSelect.item_attribute_id) {
            varion_ids.push(itemVarionDefaultSelect._id);
            varion_name.push(itemVarionDefaultSelect.name);
            varion_price.push(itemVarionDefaultSelect.price);
            varion_attribute.push(itemVarionDefaultSelect.item_attribute_id);
            varion_attribute_name.push(element.name);
            itemVariationTotal += itemVarionDefaultSelect.price;
            itemTotal += itemVarionDefaultSelect.price;
          }
        });

        itemVariation = {
          _ids: varion_ids,
          name: varion_name,
          price: varion_price,
          attribute_ids: varion_attribute,
          attribute_name: varion_attribute_name,
        };
      } else if (selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 0) {
        itemVariation = {
          _ids: [selectProductData?.item_variations[0]._id],
          name: [selectProductData?.item_variations[0].name],
          price: [selectProductData?.item_variations[0].price],
          attribute_ids: [selectProductData?.item_variations[0].item_attribute_id],
          attribute_name: [selectProductData?.item_variation_attributes[0].name],
        };
        itemVariationTotal = selectProductData?.item_variations[0].price;
        itemTotal += selectProductData?.item_variations[0].price;
      }

      const itemPrice =
        selectProductData?.price - (selectProductData?.price * (selectProductData?.offer?.discount_per || 0)) / 100;
      const itemTax = itemPrice >= 1 ? ((itemPrice * (selectProductData?.item_tax?.tax_rate || 0)) / 100) : 0;
      itemTotal += (itemPrice + itemTax) * 1;

      setAddToCartItem({
        _id: selectProductData?._id,
        name: selectProductData?.name,
        file_name: selectProductData?.file_name,
        quantity: 1,
        price: itemPrice + itemTax,
        tax_name: itemPrice >= 1 ? selectProductData?.item_tax?.name : '',
        tax_code: itemPrice >= 1 ? selectProductData?.item_tax?.code : '',
        tax_rate: itemPrice >= 1 ? selectProductData?.item_tax?.tax_rate : '',
        tax_amount: itemTax,
        item_variations: itemVariation,
        item_variation_total: itemVariationTotal,
        item_extras: { _ids: [], name: [], price: [] },
        item_extra_total: 0,
        total_price: itemTotal,
        instruction: '',
      });
    }
  }, [selectProductData?._id]);

  const handleModelClose = () => {
    setModalOpen(!modalOpen);
    document.body.style.overflowY = 'auto';
  };

  const cartItemChange = async (name, value) => {
    const cartItemUpdate = { ...addToCartItem };
    let itemVariationTotal = 0;
    let itemExtraTotal = 0;

    if (name === 'item_qty') {
      cartItemUpdate.quantity = value;
    }

    if (name === 'item_variation') {
      const itemVarionDefaultSelect = selectProductData?.item_variations.find((item) => item._id === value);
      const cartItemInVariosAttributeExits = cartItemUpdate?.item_variations?.attribute_ids.find(
        (item) => item === itemVarionDefaultSelect?.item_attribute_id
      );

      if (itemVarionDefaultSelect && itemVarionDefaultSelect._id) {
        if (cartItemInVariosAttributeExits) {
          const indexToRemove = cartItemUpdate?.item_variations?.attribute_ids.indexOf(cartItemInVariosAttributeExits);
          const cartItemInVariosAttribute = selectProductData?.item_variation_attributes.find(
            (item) => item?._id === itemVarionDefaultSelect?.item_attribute_id
          );
          cartItemUpdate?.item_variations?._ids.splice(indexToRemove, 1);
          cartItemUpdate?.item_variations?.name.splice(indexToRemove, 1);
          cartItemUpdate?.item_variations?.price.splice(indexToRemove, 1);
          cartItemUpdate?.item_variations?.attribute_ids.splice(indexToRemove, 1);
          cartItemUpdate?.item_variations?.attribute_name.splice(indexToRemove, 1);

          cartItemUpdate?.item_variations?._ids.push(itemVarionDefaultSelect?._id);
          cartItemUpdate?.item_variations?.name.push(itemVarionDefaultSelect?.name);
          cartItemUpdate?.item_variations?.price.push(itemVarionDefaultSelect?.price);
          cartItemUpdate?.item_variations?.attribute_ids.push(itemVarionDefaultSelect?.item_attribute_id);
          cartItemUpdate?.item_variations?.attribute_name.push(cartItemInVariosAttribute?.name);
        } else {
          const cartItemInVariosAttribute = selectProductData?.item_variation_attributes.find(
            (item) => item?._id === itemVarionDefaultSelect?.item_attribute_id
          );
          cartItemUpdate?.item_variations?._ids.push(itemVarionDefaultSelect?._id);
          cartItemUpdate?.item_variations?.name.push(itemVarionDefaultSelect?.name);
          cartItemUpdate?.item_variations?.price.push(itemVarionDefaultSelect?.price);
          cartItemUpdate?.item_variations?.attribute_ids.push(itemVarionDefaultSelect?.item_attribute_id);
          cartItemUpdate?.item_variations?.attribute_name.push(cartItemInVariosAttribute?.name);
        }
      }
    }

    if (name === 'item_extra') {
      const itemExtraDefaultSelect = selectProductData?.item_extras.find((item) => item._id === value);
      const cartItemInExtraExits = cartItemUpdate?.item_extras?._ids.find((item) => item === value);

      if (itemExtraDefaultSelect && itemExtraDefaultSelect._id) {
        if (cartItemInExtraExits) {
          const indexToRemove = cartItemUpdate?.item_extras?._ids.indexOf(cartItemInExtraExits);
          cartItemUpdate?.item_extras?._ids.splice(indexToRemove, 1);
          cartItemUpdate?.item_extras?.name.splice(indexToRemove, 1);
          cartItemUpdate?.item_extras?.price.splice(indexToRemove, 1);
        } else {
          cartItemUpdate?.item_extras?._ids.push(itemExtraDefaultSelect?._id);
          cartItemUpdate?.item_extras?.name.push(itemExtraDefaultSelect?.name);
          cartItemUpdate?.item_extras?.price.push(itemExtraDefaultSelect?.price);
        }
      }
    }

    await Promise.all(
      (cartItemUpdate?.item_variations?.price || []).map(async (element) => {
        itemVariationTotal += element;
      })
    );

    await Promise.all(
      (cartItemUpdate?.item_extras?.price || []).map(async (element) => {
        itemExtraTotal += element;
      })
    );

    const basePrice = (cartItemUpdate?.price || 0) + (itemVariationTotal || 0) + (itemExtraTotal || 0);
    const totalPrice = basePrice * (cartItemUpdate?.quantity || 1);

    cartItemUpdate.item_variation_total = itemVariationTotal;
    cartItemUpdate.item_extra_total = itemExtraTotal;
    cartItemUpdate.total_price = totalPrice;

    setAddToCartItem(cartItemUpdate);
  };

  const onSubmit = (data) => {
    const cartItemAdd = { ...addToCartItem };
    cartItemAdd.instruction = data.instruction;
    dispatch(addOrUpdateCartItemTable(cartItemAdd));
    handleModelClose();
    setSelectProductData('');
    ToastMe('Successfully add to cart', 'success');
  };

  return (
    <CommonModal modelTitle={'Beef with Mix Vegetables'} addonsclassName={'ff-modal'} modelHeaderHide={true} openModel={modalOpen} handleModelClose={handleModelClose}>
      <div className="modal-dialog max-w-[647px]">
        <div className="modal-header items-start border-none pb-0">
          <div className="flex gap-4">
            <img className="flex-shrink-0 w-[72px] h-[72px] object-cover rounded-lg" src={getFileUrl(selectProductData?.file_name, 'product')} alt="thumbnail" />
            <div className="flex-auto">
              <div className="flex items-start gap-2 mb-1">
                <h3 className="text-sm font-semibold capitalize">{selectProductData?.name}</h3>
                <button type="button" className="info-btn mt-0.5" onClick={() => handleProductCauction(selectProductData)}>
                  <i className="lab lab-information font-fill-paragraph transition lab-font-size-16"></i>
                </button>
              </div>
              <p className="text-xs mb-2">{selectProductData?.description}</p>
              <h4 className="text-sm font-semibold">
                <CurrencyCodeAdd price={selectProductData?.price - (selectProductData?.price * (selectProductData?.offer?.discount_per || 0)) / 100} />
              </h4>
            </div>
          </div>
          <button className="modal-close lab-close-circle-line font-fill-danger lab-font-size-24" onClick={() => handleModelClose()}></button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm leading-6 font-medium first-letter:uppercase text-heading">Quantity:</h3>
              <InDecGroupBox inputName="item_qty" onChangeValue={cartItemChange} groupClassName={'py-1 px-2 rounded-xl bg-[#F7F7FC]'} buttonClassName={'w-[18px] h-[18px] text-[10px]'} />
            </div>

            <div className="mb-4">
              {selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 1 ? (
                <div className="mb-4">
                  <div className="row relative z-[2]">
                    {selectProductData?.item_variation_attributes?.map((variationAttribute, index) => (
                      <div className="col-12 sm:col-6" key={variationAttribute?._id || index}>
                        <Controller
                          control={control}
                          name={variationAttribute?.name}
                          render={({ field }) => (
                            <InputSelect
                              {...field}
                              id={variationAttribute?.name}
                              size="sm"
                              rules={{ required: true }}
                              labelText={variationAttribute?.name}
                              name={variationAttribute?.name}
                              labelStyleClassName="text-sm leading-6 block font-medium capitalize mb-1.5 text-heading"
                              options={selectProductData?.item_variations
                                ?.filter((variation) => variation?.item_attribute_id === variationAttribute?._id)
                                .map((variation) => ({
                                  value: variation._id,
                                  label: `${variation.name} + ${formatCurrency(variation.price, generalSetting)}`,
                                })) }
                              value={
                                selectProductData?.item_variations
                                  ?.filter((variation) => variation?.item_attribute_id === variationAttribute?._id)
                                  .map((variation) => ({
                                    value: variation._id,
                                    label: `${variation.name} + ${formatCurrency(variation.price, generalSetting)}`,
                                  }))
                                  ?.find((option) => addToCartItem?.item_variations?._ids?.some((id) => id === option.value)) || null
                              }
                              onChange={(e) => cartItemChange('item_variation', e)}
                              errorType={errors?.[variationAttribute?.name]?.type}
                            />
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 0 ? (
                <>
                  <h3 className="text-sm leading-6 font-medium capitalize mb-2 text-heading">{selectProductData?.item_variation_attributes[0]?.name}</h3>
                  <div className="swiper size-swiper">
                    <div className="size-tabs">
                      <Swiper speed={1000} slidesPerView="auto" spaceBetween={16}>
                        {selectProductData?.item_variations?.map((variation, index) => (
                          <SwiperSlide className="!w-fit" key={variation?._id || index}>
                            <InputRadio
                              groupClassName={cn(
                                'variation-margin-right w-full h-[52px] cursor-pointer py-2 px-3 gap-2 rounded-lg flex items-center border transition border-[#F7F7FC] bg-[#F7F7FC]',
                                addToCartItem?.item_variations?._ids?.some((id) => id === variation._id) && 'active'
                              )}
                              inputClassName="sm"
                              labelClassName={'!ps-0'}
                              name="sizeVariation"
                              checked={!!addToCartItem?.item_variations?._ids?.some((id) => id === variation._id)}
                              onClick={() => cartItemChange('item_variation', variation._id)}
                              labelText={
                                <>
                                  <span className="block capitalize text-xs text-heading">{variation?.name}</span>
                                  {variation?.price && (
                                    <span className="block text-xs font-medium text-heading">
                                      <CurrencyCodeAdd price={variation?.price} />
                                    </span>
                                  )}
                                </>
                              }
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {selectProductData?.item_extras && selectProductData?.item_extras.length > 0 ? (
              <div className="mb-4">
                <h3 className="text-sm leading-6 font-medium capitalize mb-2 text-heading">Extras</h3>
                <div className="extra-swiper">
                  <Swiper speed={1000} slidesPerView="auto" spaceBetween={16}>
                    {selectProductData?.item_extras?.map((extra, index) => (
                      <SwiperSlide className="!w-fit !relative" key={extra?._id || index}>
                        <InputCheck
                          name={extra.name}
                          groupClassName={cn(
                            'extra w-full h-[52px] cursor-pointer py-2 px-3 gap-3 rounded-lg flex items-center border transition border-[#F7F7FC] bg-[#F7F7FC]',
                            addToCartItem?.item_extras?._ids.find((item) => item === extra._id) && 'active'
                          )}
                          inputClassName={'w-3 h-3'}
                          labelClassName="!ps-0"
                          onClick={() => cartItemChange('item_extra', extra._id)}
                          checked={!!addToCartItem?.item_extras?._ids.find((item) => item === extra._id)}
                          labelText={
                            <>
                              <span className="block capitalize mb-1 text-xs text-heading">{extra.name}</span>
                              {extra?.price && (
                                <span className="block text-xs font-medium text-heading">
                                  <CurrencyCodeAdd price={extra?.price} />
                                </span>
                              )}
                            </>
                          }
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            ) : null}

            <div className="mb-6">
              <Controller
                control={control}
                name={'instruction'}
                render={({ field }) => (
                  <InputTextarea
                    {...field}
                    labelStyleClassName="text-xs leading-6 font-medium capitalize mb-2 text-heading"
                    labelText="Special Instructions"
                    placeholder="Add Note"
                    className="rounded-lg border py-1.5 px-2 placeholder:text-[10px] placeholder:text-[#6E7191]"
                  />
                )}
              />
            </div>

            <button type="submit" className="flex items-center justify-center gap-3 rounded-3xl text-base py-3 px-3 font-medium w-full text-white bg-primary">
              <i className="lab-bag-2"></i>
              <span>
                Add to Cart - <CurrencyCodeAdd price={addToCartItem?.total_price} />
              </span>
            </button>
          </form>
        </div>
      </div>
    </CommonModal>
  );
};

export default ProductInfoModal;
